'use client';

import { useEffect, useRef, useState } from 'react';

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'error';

export interface StreamStats {
  /** (Δ jitterBufferDelay / Δ jitterBufferEmittedCount) * 1000 */
  jitterBufferDelayMs: number;
  /** (Δ totalDecodeTime / Δ framesDecoded) * 1000 */
  decodeTimeMs: number;
  fps: number;
  bitrateMbps: number;
  packetsLost: number;
  framesDropped: number;
  /** currentRoundTripTime * 1000 from the active candidate-pair */
  rttMs: number;
  width: number;
  height: number;
  /** Seconds of video sitting in the element buffer ahead of playback. */
  bufferAheadSec: number;
}

interface DroneStreamPlayerProps {
  whepUrl: string;
  onStats?: (stats: StreamStats) => void;
  onStatusChange?: (status: ConnectionStatus, error?: string) => void;
  /** Bump this number to force a manual re-handshake. */
  reconnectToken?: number;
  /** Receives the underlying <video> element (the detection overlay needs its videoWidth/Height). */
  onVideoElement?: (el: HTMLVideoElement | null) => void;
  className?: string;
}

/** Receiver fields that are not yet in every TS lib.dom. */
type LowLatencyReceiver = RTCRtpReceiver & {
  jitterBufferTarget?: number | null;
  playoutDelayHint?: number | null;
};

const MAX_BACKOFF_MS = 15_000;
const ICE_GATHER_TIMEOUT_MS = 1_000;
const CATCH_UP_THRESHOLD_SEC = 0.1;

function waitForIceGathering(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(done, ICE_GATHER_TIMEOUT_MS);
    function done() {
      clearTimeout(timer);
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    }
    function check() {
      if (pc.iceGatheringState === 'complete') done();
    }
    pc.addEventListener('icegatheringstatechange', check);
  });
}

export default function DroneStreamPlayer({
  whepUrl,
  onStats,
  onStatusChange,
  reconnectToken = 0,
  onVideoElement,
  className = '',
}: DroneStreamPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [localToken, setLocalToken] = useState(0);

  useEffect(() => {
    onVideoElement?.(videoRef.current);
    return () => onVideoElement?.(null);
  }, [onVideoElement]);

  // Keep latest callbacks without re-running the connection effect.
  const onStatsRef = useRef(onStats);
  const onStatusRef = useRef(onStatusChange);
  useEffect(() => {
    onStatsRef.current = onStats;
    onStatusRef.current = onStatusChange;
  }, [onStats, onStatusChange]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let disposed = false;
    let pc: RTCPeerConnection | null = null;
    let sessionUrl: string | null = null;
    let statsTimer: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const prev = {
      t: 0,
      bytesReceived: 0,
      jitterBufferDelay: 0,
      jitterBufferEmittedCount: 0,
      totalDecodeTime: 0,
      framesDecoded: 0,
    };

    const update = (s: ConnectionStatus, err?: string) => {
      if (disposed) return;
      setStatus(s);
      setError(err ?? null);
      onStatusRef.current?.(s, err);
    };

    const teardown = () => {
      if (statsTimer) clearInterval(statsTimer);
      statsTimer = null;
      if (pc) {
        pc.ontrack = null;
        pc.onconnectionstatechange = null;
        pc.oniceconnectionstatechange = null;
        pc.close();
        pc = null;
      }
      if (sessionUrl) {
        fetch(sessionUrl, { method: 'DELETE' }).catch(() => undefined);
        sessionUrl = null;
      }
      video.srcObject = null;
    };

    const scheduleReconnect = (reason: string) => {
      if (disposed || reconnectTimer) return;
      const delay = Math.min(500 * 2 ** attempt, MAX_BACKOFF_MS);
      attempt += 1;
      update('reconnecting', `${reason} — retrying in ${(delay / 1000).toFixed(1)}s`);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        teardown();
        void connect();
      }, delay);
    };

    const collectStats = async () => {
      if (!pc || disposed) return;
      let report: RTCStatsReport;
      try {
        report = await pc.getStats();
      } catch {
        return;
      }
      const now = performance.now();
      const dt = prev.t ? (now - prev.t) / 1000 : 0;

      let inbound: Record<string, number | string | undefined> | undefined;
      let selectedPairId: string | undefined;
      const pairs = new Map<string, Record<string, number | string | boolean | undefined>>();

      report.forEach((r) => {
        if (r.type === 'inbound-rtp' && r.kind === 'video') inbound = r;
        else if (r.type === 'transport' && r.selectedCandidatePairId) selectedPairId = r.selectedCandidatePairId;
        else if (r.type === 'candidate-pair') pairs.set(r.id, r);
      });

      let pair = selectedPairId ? pairs.get(selectedPairId) : undefined;
      if (!pair) {
        for (const p of pairs.values()) {
          if (p.nominated && p.state === 'succeeded') {
            pair = p;
            break;
          }
        }
      }

      if (!inbound) return;

      const bytes = Number(inbound.bytesReceived ?? 0);
      const jbd = Number(inbound.jitterBufferDelay ?? 0);
      const jbc = Number(inbound.jitterBufferEmittedCount ?? 0);
      const tdt = Number(inbound.totalDecodeTime ?? 0);
      const fd = Number(inbound.framesDecoded ?? 0);

      const dJbc = jbc - prev.jitterBufferEmittedCount;
      const dFd = fd - prev.framesDecoded;

      let bufferAheadSec = 0;
      if (video.buffered.length > 0) {
        const end = video.buffered.end(video.buffered.length - 1);
        bufferAheadSec = end - video.currentTime;
        // Lag catch-up: snap playhead to the live edge if drift exceeds threshold.
        if (bufferAheadSec > CATCH_UP_THRESHOLD_SEC && !video.paused) {
          video.currentTime = end;
        }
      }

      const stats: StreamStats = {
        jitterBufferDelayMs: dJbc > 0 ? ((jbd - prev.jitterBufferDelay) / dJbc) * 1000 : 0,
        decodeTimeMs: dFd > 0 ? ((tdt - prev.totalDecodeTime) / dFd) * 1000 : 0,
        fps: Number(inbound.framesPerSecond ?? 0),
        bitrateMbps: dt > 0 ? ((bytes - prev.bytesReceived) * 8) / dt / 1_000_000 : 0,
        packetsLost: Number(inbound.packetsLost ?? 0),
        framesDropped: Number(inbound.framesDropped ?? 0),
        rttMs: pair?.currentRoundTripTime != null ? Number(pair.currentRoundTripTime) * 1000 : 0,
        width: Number(inbound.frameWidth ?? video.videoWidth ?? 0),
        height: Number(inbound.frameHeight ?? video.videoHeight ?? 0),
        bufferAheadSec,
      };

      prev.t = now;
      prev.bytesReceived = bytes;
      prev.jitterBufferDelay = jbd;
      prev.jitterBufferEmittedCount = jbc;
      prev.totalDecodeTime = tdt;
      prev.framesDecoded = fd;

      onStatsRef.current?.(stats);
    };

    const connect = async () => {
      if (disposed) return;
      update(attempt === 0 ? 'connecting' : 'reconnecting');

      const conn = new RTCPeerConnection({ bundlePolicy: 'max-bundle' });
      pc = conn;

      conn.addTransceiver('video', { direction: 'recvonly' });
      conn.addTransceiver('audio', { direction: 'recvonly' });

      conn.ontrack = (ev) => {
        if (ev.track.kind === 'video') {
          const rx = ev.receiver as LowLatencyReceiver;
          try {
            rx.jitterBufferTarget = 0;
          } catch {
            /* unsupported */
          }
          try {
            rx.playoutDelayHint = 0;
          } catch {
            /* unsupported */
          }
        }
        if (video.srcObject !== ev.streams[0]) {
          video.srcObject = ev.streams[0] ?? new MediaStream([ev.track]);
          video.play().catch(() => undefined);
        }
      };

      const onStateChange = () => {
        if (pc !== conn || disposed) return;
        const s = conn.connectionState;
        const ice = conn.iceConnectionState;
        if (s === 'connected' || ice === 'connected' || ice === 'completed') {
          attempt = 0;
          update('connected');
        } else if (s === 'failed' || ice === 'failed' || s === 'disconnected' || ice === 'disconnected') {
          scheduleReconnect(`Connection ${s === 'failed' || ice === 'failed' ? 'failed' : 'lost'}`);
        }
      };
      conn.onconnectionstatechange = onStateChange;
      conn.oniceconnectionstatechange = onStateChange;

      try {
        const offer = await conn.createOffer();
        await conn.setLocalDescription(offer);
        await waitForIceGathering(conn);
        if (disposed || pc !== conn) return;

        const res = await fetch(whepUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/sdp' },
          body: conn.localDescription?.sdp ?? offer.sdp,
        });
        if (!res.ok) throw new Error(`WHEP ${res.status} ${res.statusText}`);

        const location = res.headers.get('Location');
        if (location) sessionUrl = new URL(location, whepUrl).toString();

        const answer = await res.text();
        if (disposed || pc !== conn) return;
        await conn.setRemoteDescription({ type: 'answer', sdp: answer });

        if (statsTimer) clearInterval(statsTimer);
        prev.t = 0;
        statsTimer = setInterval(() => void collectStats(), 1_000);
      } catch (e) {
        if (disposed || pc !== conn) return;
        const msg = e instanceof Error ? e.message : String(e);
        update('error', msg);
        scheduleReconnect(msg);
      }
    };

    void connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      teardown();
    };
  }, [whepUrl, reconnectToken, localToken]);

  return (
    <div className={`relative h-full w-full overflow-hidden bg-black ${className}`}>
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="h-full w-full object-contain"
      />

      {(status === 'connecting' || status === 'reconnecting') && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 backdrop-blur-sm">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-600 border-t-cyan-400" />
          <p className="text-sm font-medium tracking-wide text-slate-300">
            {status === 'connecting' ? 'Connecting to stream…' : 'Reconnecting…'}
          </p>
          {error && <p className="text-xs text-slate-500">{error}</p>}
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-4 bg-red-600/90 px-4 py-2 text-sm text-white shadow-lg">
          <span className="truncate">
            <span className="font-semibold">Stream error:</span> {error ?? 'unknown'}
          </span>
          <button
            type="button"
            onClick={() => setLocalToken((n) => n + 1)}
            className="shrink-0 rounded bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide hover:bg-white/25"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
